// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import Image from 'presentations/views/components/atoms/Image';
import Link from 'presentations/views/components/atoms/Link';
import Icon from 'presentations/views/components/atoms/Icon';
import { IntegrationType } from 'domains/models/account/providerType';

import {
  primary,
  gray04,
  gray01,
  gray02,
  black,
} from 'presentations/views/styles/color';
import {
  fontSize,
  spacing,
  mediaMax,
  fontWeight,
  borderRadius,
} from 'presentations/views/styles';
import { PlanId } from 'domains/models/subscription/planId';
import FormLabel from 'presentations/views/components/molecules/FormLabel';
import SalesforceIntegrationFeature from 'domains/models/subscription/features/integrations/salesforce';
import { PlanVersion } from 'domains/models/subscription/planVersion';
import ConnectIcon from 'presentations/views/components/atoms/ConnectIcon';

interface Props {
  isLogin: boolean;
  isConnect?: boolean;
  isDisabled?: boolean;
  name: string;
  subName?: string;
  image: string;
  description: string;
  link?: string;
  hasSetting?: boolean;
  integrationType?: IntegrationType;
  planId?: PlanId;
  planVersion?: PlanVersion;
  onClickLink?: Function;
  minimumPlanId?: PlanId;
  isShowPricingLink?: boolean;
  isAdminRequired?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  let isValidPlan = false;
  if (props.planId && props.planVersion) {
    const salesforceIntegrationFeature = new SalesforceIntegrationFeature({
      planVersion: props.planVersion,
    });
    isValidPlan = salesforceIntegrationFeature.isValid(props.planId);
  }
  const isShowConnect =
    props.integrationType !== IntegrationType.Salesforce ||
    (props.integrationType === IntegrationType.Salesforce && isValidPlan);

  return (
    <AppListItem>
      {props.isAdminRequired && <AdminIcon>{t('Admin Required')}</AdminIcon>}
      <AppNameContainer>
        <AppImageContainer>
          <AppImage src={props.image} alt={props.name} />
        </AppImageContainer>
        <AppTitleWrapper>
          <AppTitle>{props.name}</AppTitle>
          {props.subName && <AppSubTitle>{props.subName}</AppSubTitle>}
        </AppTitleWrapper>
      </AppNameContainer>
      {props.isLogin && props.integrationType && (
        <>
          {isShowConnect ? (
            <FormLabelContainer>
              <ButtonInner>
                <ConnectIcon isConnect={props.isConnect} />
              </ButtonInner>
            </FormLabelContainer>
          ) : (
            <FormLabelContainer>
              <FormLabel
                isPaid={true}
                isShowPaidLink={true}
                plan={PlanId.Team}
              />
            </FormLabelContainer>
          )}
        </>
      )}
      {props.isShowPricingLink && (
        <FormLabel
          isPaid={true}
          isShowPaidLink={true}
          plan={props.minimumPlanId}
        />
      )}
      <AppDescription>{props.description}</AppDescription>
      {props.isLogin && props.link && (
        <AppLinkContainer>
          <AppLinkIcon name="chevron-right" />
          <DetailLink href={props.link} onClick={props.onClickLink}>
            {t('LinkTitle')}
          </DetailLink>
        </AppLinkContainer>
      )}
    </AppListItem>
  );
}
const AppListItem = styled.div`
  height: 100%;
  border: 1px solid ${gray04};
  padding: ${spacing(48)} ${spacing(24)};
  position: relative;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;
const AppTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  ${mediaMax.tablet} {
    flex-direction: column;
    justify-content: center;
  }
`;
const AppTitle = styled.div`
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
`;
const AppSubTitle = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  margin-left: ${spacing(12)};
  white-space: nowrap;
  ${mediaMax.tablet} {
    margin-left: 0;
  }
`;
const AppNameContainer = styled.div`
  margin-bottom: ${spacing(16)};
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const AppLinkIcon = styled(Icon)`
  font-size: ${fontSize(14)};
  color: ${primary};
`;
const AppImageContainer = styled.div`
  height: 60px;
  overflow: none;
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(8)};
`;
const AppImage = styled(Image)`
  height: 60px;
`;
const AppDescription = styled.div`
  margin-bottom: ${spacing(32)};
  text-align: left;
`;
const AppLinkContainer = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 32px;
  justify-content: center;
`;

const FormLabelContainer = styled.div`
  position: relative;
  top: inherit;
  left: ${spacing(3)};
  margin-bottom: ${spacing(20)};
  ${mediaMax.tablet} {
    left: ${spacing(36)};
    max-width: 150px;
    width: 100%;
  }
`;
const ButtonInner = styled.p`
  display: flex;
  align-items: center;
  line-height: 1.2;
  color: ${gray01};
  ${mediaMax.xs} {
    white-space: pre-wrap;
  }
`;

const DetailLink = styled(Link)`
  color: ${black};
`;

const AdminIcon = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${gray02};
  border-radius: ${borderRadius(4)};
  border: 1px solid ${gray02};
  margin-left: ${spacing(16)};
  font-size: ${fontSize(11)};
  width: fit-content;
  height: fit-content;
  padding: 1px ${spacing(8)};
  ${mediaMax.tablet} {
    margin-left: 0;
    margin-top: ${spacing(8)};
  }
`;
